import { memo } from 'react';
import { Handle, Position, NodeProps } from 'reactflow';
import {
  Zap,
  Filter,
  Code2,
  GitBranch,
  Play,
  Settings,
  Workflow,
} from 'lucide-react';
import { FlowNode } from '../types/zapier';
import { parseFilterConditions, hasFilterConditions } from '../utils/filterParser';
import { getAppIcon } from '../utils/appIcons';

const nodeTypeIcons = {
  trigger: Play,
  action: Settings,
  filter: Filter,
  code: Code2,
  branch: GitBranch,
  unknown: Workflow,
};

const nodeTypeColors = {
  trigger: 'bg-emerald-500/10 border-emerald-500 text-emerald-700 dark:text-emerald-400',
  action: 'bg-blue-500/10 border-blue-500 text-blue-700 dark:text-blue-400',
  filter: 'bg-amber-500/10 border-amber-500 text-amber-700 dark:text-amber-400',
  code: 'bg-purple-500/10 border-purple-500 text-purple-700 dark:text-purple-400',
  branch: 'bg-pink-500/10 border-pink-500 text-pink-700 dark:text-pink-400',
  unknown: 'bg-gray-500/10 border-gray-500 text-gray-700 dark:text-gray-400',
};

export const ZapNode = memo(({ data, selected }: NodeProps<FlowNode['data']>) => {
  const Icon = nodeTypeIcons[data.nodeType];
  const colorClass = nodeTypeColors[data.nodeType];

  // Parse filter conditions for filter nodes
  const rawNodeParams = 'params' in data.rawNode ? data.rawNode.params : undefined;
  const filterInfo = data.nodeType === 'filter' && hasFilterConditions(data.config, rawNodeParams)
    ? parseFilterConditions(data.config, rawNodeParams)
    : null;

  // Get app icon
  const appIcon = getAppIcon(data.app);

  return (
    <div
      className={`
        relative px-4 py-3 rounded-lg border-2 bg-card
        transition-all duration-200 min-w-[280px] max-w-[320px]
        ${colorClass}
        ${selected ? 'ring-2 ring-primary ring-offset-2 ring-offset-background shadow-xl scale-105' : 'shadow-md hover:shadow-lg'}
      `}
    >
      {/* Input Handle */}
      {data.nodeType !== 'trigger' && (
        <Handle
          type="target"
          position={Position.Top}
          className="!w-3 !h-3 !bg-primary !border-2 !border-background"
        />
      )}

      {/* Node Content */}
      <div className="flex items-start gap-3">
        <div className="flex-shrink-0 flex flex-col items-center gap-1">
          {/* App emoji icon */}
          <div
            className="w-8 h-8 rounded-md flex items-center justify-center text-lg bg-background/50 border border-border/50"
            title={data.app}
          >
            {appIcon.emoji}
          </div>
          {/* Node type icon */}
          <Icon className="w-4 h-4 opacity-70" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <span className="text-xs font-medium uppercase tracking-wide opacity-70">
              {data.nodeType}
            </span>
          </div>
          <p className="font-semibold text-sm leading-tight mb-1 text-card-foreground">
            {data.label}
          </p>
          <p className="text-xs text-muted-foreground truncate">{data.app}</p>

          {/* Filter node badge */}
          {filterInfo && (
            <div className="flex items-center gap-1.5 mt-1.5">
              <span className="inline-flex items-center px-1.5 py-0.5 text-[10px] font-semibold bg-amber-200 dark:bg-amber-900/40 text-amber-900 dark:text-amber-200 rounded">
                {filterInfo.conditionCount} {filterInfo.conditionCount === 1 ? 'condition' : 'conditions'}
              </span>
              {filterInfo.conditionCount > 1 && (
                <span className="inline-flex items-center px-1.5 py-0.5 text-[10px] font-medium bg-muted text-muted-foreground rounded">
                  {filterInfo.logic}
                </span>
              )}
            </div>
          )}
        </div>
        {data.isLocked && (
          <Zap className="w-4 h-4 text-amber-500 flex-shrink-0" />
        )}
      </div>

      {/* Output Handle */}
      <Handle
        type="source"
        position={Position.Bottom}
        className="!w-3 !h-3 !bg-primary !border-2 !border-background"
      />
    </div>
  );
});

ZapNode.displayName = 'ZapNode';
